import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  getProductsList,
  productsSelector,
  deleteBulkProduct,
  toggleActiveBulkProduct,
  changeQuery,
  clearPublishValidationError,
} from '../../feature';

// Components
import {
  StyledHeaderBlock,
  StyledPageTitle,
  StyledTitleBlock,
  StyledZeroPriceFilter,
  StyledPageWrapper,
  StyledContentBlock,
  StyledTitleModal,
  StyledButtonsWrapper,
  StyledActionsWrap,
  StyledClearButton,
  StyledSelect,
} from './ProductsListContainerStyles';
import { Button, Checkbox } from '@/ui';
import { Modal, ModalSize } from '@/components';
import { DeleteProductForbiddenModal, ProductsList, PublishValidationModal } from '../../components';

// Models
import { IProduct } from '@/models/products.model';
import { ROUTES } from '@/models/router.model';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helper
import { useGridQuerySync, useModal } from '@/hooks';
import { confirmAction, isApiErrorEntity } from '@/utils';
import { GetProductsListDTO } from '../../dtos';
import { getBulkActionOptions, BulkAction, ProductsFiltersFields, TGetProductsQuery } from '../../helpers';
import { useIdsManager } from '@/context';
import { ResponseStatusCodes } from '@/constants/common';

const ProductsListContainer = () => {
  const { t } = useTranslation(['products']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageProduct = canManage(Resource.PRODUCT);
  const [currentProduct, setCurrentProduct] = useState<IProduct | null>(null);
  const [forbiddenDeleteMessage, setForbiddenDeleteMessage] = useState('');
  const { isOpened: isOpenForbiddenModal, openModal: openForbiddenModal, closeModal: closeForbiddenModal } = useModal();
  const { isOpened, openModal, closeModal } = useModal();
  const { isLoading, productsList, query, isPublishValidationError } = useAppState(productsSelector);

  const currentIds = useMemo(() => productsList?.data.map(({ id }) => id), [productsList?.data]);
  const { ids, setIds, clear, turnOffSelectedAll, selectedAll, toggleSelectAll } = useIdsManager();

  const getProductsListHandler = (params: GetProductsListDTO) => {
    dispatch(getProductsList(params));
  };

  const handlePageChange = (page: number) => {
    const dto = new GetProductsListDTO({
      ...query,
      page,
    });

    dispatch(
      changeQuery({
        ...query,
        page,
      }),
    );

    getProductsListHandler(dto);
  };

  const onCancelSelect = () => {
    clear();
    turnOffSelectedAll();
  };

  const onToggleSelectAll = () => {
    setIds(selectedAll ? [] : currentIds ?? []);
    toggleSelectAll();
  };

  const handleCreateProductButton = () => navigate(ROUTES.PRODUCT_CREATE);

  const handleDelete = async () => {
    try {
      if (currentProduct) await dispatch(deleteBulkProduct({ ids: [currentProduct.id] })).unwrap();
      const params = new GetProductsListDTO({ ...query });
      getProductsListHandler(params);
      closeModal();
      setCurrentProduct(null);
    } catch (error) {
      closeModal();
      if (isApiErrorEntity(error) && error?.status === ResponseStatusCodes.UNPROCESSABLE_CONTENT) {
        setForbiddenDeleteMessage(error.message);
        openForbiddenModal();
      }
    }
  };

  const changeProductForDelete = (product: IProduct) => {
    setCurrentProduct(product);
    openModal();
  };

  const handleToggleActive = async (product: IProduct) => {
    try {
      dispatch(toggleActiveBulkProduct({ ids: [product.id] }));
      closeModal();
      setCurrentProduct(null);
    } catch {}
  };

  const handleBulkAction = async (action: unknown) => {
    try {
      switch (action as BulkAction) {
        case BulkAction.DELETE:
          await confirmAction(t('products_list_delete_message'), () =>
            dispatch(deleteBulkProduct({ ids, all: selectedAll, ...query })).unwrap(),
          );
          handlePageChange(1);
          break;
        case BulkAction.HIDE:
          await confirmAction(t('products_list_hide_message'), async () => {
            try {
              await dispatch(toggleActiveBulkProduct({ ids, all: selectedAll, ...query })).unwrap();
            } catch {
              // PublishValidationModal / toasts are handled in the products slice
            }
          });
          handlePageChange(1);
          break;
        default:
          return;
      }
    } catch (error) {
      if (isApiErrorEntity(error) && error?.status === ResponseStatusCodes.UNPROCESSABLE_CONTENT) {
        setForbiddenDeleteMessage(error.message);
        openForbiddenModal();
      } else {
        console.error('Error during bulk action:', error);
      }
    }
  };

  const handleCloseModal = () => {
    closeModal();
    setCurrentProduct(null);
  };

  const handleClosePublishValidationModal = () => {
    dispatch(clearPublishValidationError());
  };

  const restoreFromUrl = (restoredQuery: TGetProductsQuery) => {
    dispatch(changeQuery(restoredQuery));
    getProductsListHandler(new GetProductsListDTO(restoredQuery));
  };

  useGridQuerySync(query as TGetProductsQuery, restoreFromUrl, {
    arrayKeys: [ProductsFiltersFields.CATEGORY_IDS, ProductsFiltersFields.PARTNER_IDS],
    numberKeys: ['page', ProductsFiltersFields.LIMIT],
  });

  const handleQuickFilter =
    (
      field:
        | ProductsFiltersFields.ONLY_ZERO_PRICE
        | ProductsFiltersFields.ONLY_WITHOUT_UKTZED
        | ProductsFiltersFields.ONLY_WITHOUT_DIMENSIONS,
    ) =>
    (e: CheckboxChangeEvent) => {
      const newQuery = {
        ...query,
        [field]: e.target.checked ? ('1' as const) : undefined,
        page: 1,
      };
      const dto = new GetProductsListDTO(newQuery);
      getProductsListHandler(dto);
      dispatch(changeQuery(dto));
    };

  useEffect(() => {
    return () => {
      dispatch(changeQuery({}));
    };
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledTitleBlock>
          <StyledPageTitle>{t('products_tab_title')}</StyledPageTitle>
          <StyledZeroPriceFilter>
            <Checkbox
              checked={query?.[ProductsFiltersFields.ONLY_ZERO_PRICE] === '1'}
              onChange={handleQuickFilter(ProductsFiltersFields.ONLY_ZERO_PRICE)}
            />
            <span>{t('products_list_only_zero_price')}</span>
          </StyledZeroPriceFilter>
          <StyledZeroPriceFilter>
            <Checkbox
              checked={query?.[ProductsFiltersFields.ONLY_WITHOUT_UKTZED] === '1'}
              onChange={handleQuickFilter(ProductsFiltersFields.ONLY_WITHOUT_UKTZED)}
            />
            <span>{t('products_list_only_without_uktzed')}</span>
          </StyledZeroPriceFilter>
          <StyledZeroPriceFilter>
            <Checkbox
              checked={query?.[ProductsFiltersFields.ONLY_WITHOUT_DIMENSIONS] === '1'}
              onChange={handleQuickFilter(ProductsFiltersFields.ONLY_WITHOUT_DIMENSIONS)}
            />
            <span>{t('products_list_only_without_dimensions')}</span>
          </StyledZeroPriceFilter>
        </StyledTitleBlock>

        {canManageProduct && (
          <StyledActionsWrap>
            {!selectedAll && ids.length !== productsList?.meta?.total && (
              <StyledClearButton onClick={onToggleSelectAll}>{t('products_list_select_all')}</StyledClearButton>
            )}
            {!!ids?.length && (
              <>
                <StyledClearButton onClick={onCancelSelect}>{t('products_list_cancel')}</StyledClearButton>
                <StyledSelect
                  options={getBulkActionOptions(t)}
                  placeholder={t('products_list_choose_action')}
                  onSelect={handleBulkAction}
                />
              </>
            )}
            <StyledClearButton type="primary" onClick={handleCreateProductButton}>
              {t('create_product_title')}
            </StyledClearButton>
          </StyledActionsWrap>
        )}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <ProductsList
          productsList={productsList?.data as IProduct[]}
          isLoading={isLoading}
          setProductForDelete={changeProductForDelete}
          onPageChange={handlePageChange}
          currentPage={productsList?.meta?.current_page ?? 1}
          total={productsList?.meta?.total ?? 0}
          pageSize={productsList?.meta?.per_page ?? 10}
          handleToggleActive={handleToggleActive}
          canManageProduct={canManageProduct}
        />
      </StyledContentBlock>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>{t('products_delete_title')}</StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('products_delete_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('products_delete')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
      <DeleteProductForbiddenModal
        isOpenModal={isOpenForbiddenModal}
        closeModal={closeForbiddenModal}
        message={forbiddenDeleteMessage}
      />
      <PublishValidationModal isOpen={isPublishValidationError} onClose={handleClosePublishValidationModal} />
    </StyledPageWrapper>
  );
};

export default ProductsListContainer;
